/**
 * ============================================================================
 * UXP INPUT COMPONENTS UTILITIES (v5)
 * ============================================================================
 *
 * This module provides shared utilities for all UXP input components including
 * sizing props, state props, and the external trigger pattern for dropdown-based inputs.
 *
 * ## EXTERNAL TRIGGER PATTERN FOR DROPDOWN-BASED INPUTS
 *
 * All dropdown-based inputs (Select, DatePicker, TimePicker, ColorPicker, etc.) use
 * the external trigger pattern where the trigger and dropdown are siblings, not parent-child.
 *
 * ### Architecture:
 * ```tsx
 * <div className="uxpcore_input uxp-form-select">        // Container
 *   <div ref={triggerRef} onClick={...}>                 // External trigger
 *     ...trigger content...
 *   </div>
 *   <Dropdown externalTriggerRef={triggerRef} ... />     // Sibling dropdown
 * </div>
 * ```
 *
 * ### Benefits:
 * 1. **Full styling control** - Container uses .uxpcore_input base class
 * 2. **Cleaner architecture** - Trigger and dropdown are peers
 * 3. **Better state management** - Parent component owns state
 * 4. **Click isolation** - No double-firing issues
 *
 * ### Click Handling:
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const triggerRef = useRef<HTMLDivElement>(null);
 *
 * // Trigger handles opening/closing
 * <div ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
 *   Trigger
 * </div>
 *
 * // Dropdown handles backdrop clicks (outside clicks)
 * <Dropdown
 *   externalTriggerRef={triggerRef}
 *   isOpen={isOpen}
 *   onToggle={() => setIsOpen(!isOpen)}
 *   content={...}
 * />
 * ```
 *
 * ### Click Isolation Guarantee:
 * - ✅ Clicking trigger toggles dropdown (parent's onClick)
 * - ✅ Clicking outside closes dropdown (Dropdown's onToggle from backdrop)
 * - ✅ No double-firing: Trigger is in normal DOM, backdrop is in Portal
 * - ✅ Portal renders to document.body, separate from trigger's DOM tree
 *
 * ### Required Props for External Trigger Pattern:
 * - `externalTriggerRef` - Ref to trigger element for positioning
 * - `isOpen` - Controlled state (parent manages)
 * - `onToggle` - Callback for state changes (backdrop clicks call this)
 * - `content` - Dropdown content to render
 *
 * ### Example Implementation:
 * See `Select.tsx` for the reference implementation.
 *
 * ============================================================================
 */
/**
 * Base font size in pixels for rem calculations. Matches the desktop `--uxp-font-size`
 * (13px), so converted sizes scale with the font knob like every other rem value.
 * @const
 */
const BASE_FONT_SIZE = 13;
/**
 * Converts pixels to rem units based on BASE_FONT_SIZE
 *
 * @param px - Pixel value to convert
 * @returns rem value as string (e.g., "8.333rem")
 *
 * @example
 * ```
 * pxToRem(100); // "7.692rem"
 * pxToRem(13);  // "1rem"
 * ```
 */
export function pxToRem(px) {
    return `${(px / BASE_FONT_SIZE).toFixed(3)}rem`;
}
/**
 * Generate inline styles for input size control
 * Respects priority: style > fullWidth > width > min/maxWidth > defaults
 *
 * All number values are converted to rem units (base: 12px)
 *
 * @export
 * @example
 * ```tsx
 * const sizeStyles = getInputSizeStyles({ minWidth: 150, maxWidth: 500, style: { color: 'red' } });
 * <div style={sizeStyles}>...</div>
 * // Result: { minWidth: '12.5rem', maxWidth: '41.667rem', color: 'red' }
 * ```
 */
export const getInputSizeStyles = (props) => {
    const { fullWidth, width, minWidth, maxWidth, height, minHeight, maxHeight, style } = props;
    let sizeStyles = {};
    if (fullWidth) {
        sizeStyles.width = '100%';
        sizeStyles.minWidth = 'unset';
        sizeStyles.maxWidth = 'unset';
    }
    else if (width !== undefined) {
        const widthValue = typeof width === 'number' ? pxToRem(width) : width;
        sizeStyles.minWidth = widthValue;
        sizeStyles.maxWidth = widthValue;
    }
    else {
        if (minWidth !== undefined) {
            sizeStyles.minWidth = typeof minWidth === 'number' ? pxToRem(minWidth) : minWidth;
        }
        if (maxWidth !== undefined) {
            sizeStyles.maxWidth = typeof maxWidth === 'number' ? pxToRem(maxWidth) : maxWidth;
        }
    }
    if (height !== undefined) {
        const h = typeof height === 'number' ? pxToRem(height) : height;
        sizeStyles.height = h;
        sizeStyles.minHeight = h;
        sizeStyles.maxHeight = h;
    }
    else {
        if (minHeight !== undefined) {
            sizeStyles.minHeight = typeof minHeight === 'number' ? pxToRem(minHeight) : minHeight;
        }
        if (maxHeight !== undefined) {
            sizeStyles.maxHeight = typeof maxHeight === 'number' ? pxToRem(maxHeight) : maxHeight;
        }
    }
    return Object.assign(Object.assign({}, sizeStyles), style);
};
/**
 * Generate className string for input state (BEM modifiers)
 *
 * All state modifiers follow BEM naming: uxpcore_input--{state}
 * - uxpcore_input--disabled
 * - uxpcore_input--readonly
 * - uxpcore_input--valid
 * - uxpcore_input--invalid
 *
 * @export
 * @example
 * ```tsx
 * const stateClass = getInputStateClass({ disabled: true, isValid: false });
 * // Returns: 'uxpcore_input--disabled uxpcore_input--invalid'
 * ```
 */
export const getInputStateClass = (props) => {
    const { disabled, readOnly, isValid } = props;
    const classes = [];
    if (disabled)
        classes.push('uxpcore_input--disabled');
    if (readOnly)
        classes.push('uxpcore_input--readonly');
    if (isValid === true)
        classes.push('uxpcore_input--valid');
    if (isValid === false)
        classes.push('uxpcore_input--invalid');
    return classes.join(' ');
};
/**
 * Default input size constants in rem
 * @const
 */
export const INPUT_SIZES = {
    /** Default minimum width: 7.692rem (100px at 13px) */
    MIN_WIDTH: pxToRem(100),
    /** Default maximum width: 30.769rem (400px at 13px) */
    MAX_WIDTH: pxToRem(400),
    /** Dropdown default max width: 24.615rem (320px at 13px) */
    DROPDOWN_MAX_WIDTH: pxToRem(320),
};
